"use client";

import { useRef, useState, useCallback, useEffect } from "react";
import { Flex, Box, UnstyledButton } from "@mantine/core";
import AutocompleteSearch, { type SearchBoxRef } from "@/components/AutocompleteSearch";
import SearchPills from "@/components/SearchPills";

const HomePageSearchWithPills = () => {
  const searchRef = useRef<SearchBoxRef>(null);
  const [selectedFilters, setSelectedFilters] = useState<
    Array<{ key: string; value: string | number; label: string }>
  >([]);

  const handlePillClick = useCallback(
    (type: "brand" | "vendor" | "type" | "region" | "country", filter: Filter) => {
      searchRef.current?.selectPill(type, filter);
    },
    []
  );

  const updateSelectedFilters = useCallback(() => {
    if (searchRef.current) {
      const filters = searchRef.current.getSelected();
      setSelectedFilters(filters);
    }
  }, []);

  const handleClearAll = useCallback(() => {
    if (searchRef.current) {
      selectedFilters.forEach((filter) => {
        searchRef.current?.removePill(filter.label);
      });
      updateSelectedFilters();
    }
  }, [selectedFilters, updateSelectedFilters]);

  useEffect(() => {
    updateSelectedFilters();
  }, [updateSelectedFilters]);

  return (
    <Box w="100%" mx="auto" maw={850}>
      <Flex w="100%" my="sm" justify="center">
        <AutocompleteSearch
          ref={searchRef}
          useTypewriter
          navigateOnDropdownSelect={true}
          navigateOnSelect={false}
          onSelectionChange={updateSelectedFilters}
        />
      </Flex>

      {selectedFilters.length > 0 && (
        <Flex justify="flex-start" ml="sm" mb="sm">
          <UnstyledButton
            type="button"
            onClick={handleClearAll}
            aria-label={`Clear ${selectedFilters.length} selected filter${selectedFilters.length === 1 ? "" : "s"}`}
            style={{
              minHeight: 44,
              display: "inline-flex",
              alignItems: "center",
              color: "var(--mantine-color-dimmed)",
              fontSize: "var(--mantine-font-size-sm)",
              fontWeight: 500,
              textDecoration: "underline",
            }}
          >
            Clear filters
          </UnstyledButton>
        </Flex>
      )}

      <Box
        mt="md"
        mih={{
          base: "38px",
          md: "88px",
        }}
      >
        <SearchPills onPillClick={handlePillClick} selectedInSearchBox={selectedFilters} />
      </Box>
    </Box>
  );
};

export default HomePageSearchWithPills;
